// src/components/Enhanced3DElement.tsx
/**
 * Enhanced 3D Element Wrapper
 *
 * Adds 3D CSS transforms, physics-based animations, and particle effects
 * to make elements feel more dynamic and less predictable
 */

import React, { useState, useEffect, useRef } from 'react';
import ElementCanvas from './ElementCanvas';
import { ElementDef } from '../store/elementStore';

interface Enhanced3DElementProps {
  e: ElementDef;
  size?: number;
  enablePhysics?: boolean;
  enable3D?: boolean;
  intensity?: 'subtle' | 'medium' | 'intense';
}

const Enhanced3DElement: React.FC<Enhanced3DElementProps> = ({
  e,
  size = 64,
  enablePhysics = true,
  enable3D = true,
  intensity = 'medium',
}) => {
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isHovering, setIsHovering] = useState(false);
  const frameRef = useRef(0);
  const velocityRef = useRef({ x: 0, y: 0, rotation: 0 });
  const animationRef = useRef<number | undefined>(undefined);

  // Intensity multipliers
  const intensityMap = {
    subtle: 0.3,
    medium: 1,
    intense: 2,
  };
  const multiplier = intensityMap[intensity];

  // Physics-based floating animation
  useEffect(() => {
    if (!enablePhysics) return;

    const animate = () => {
      frameRef.current++;
      const t = frameRef.current * 0.01;

      // Floating motion with random variations
      const floatY = Math.sin(t) * 3 * multiplier;
      const floatX = Math.cos(t * 0.7) * 2 * multiplier;

      // Gentle rotation for 3D effect
      const rotateY = Math.sin(t * 0.5) * 15 * multiplier;
      const rotateX = Math.cos(t * 0.3) * 8 * multiplier;
      const rotateZ = Math.sin(t * 0.4) * 5 * multiplier;

      // Subtle scale pulsation based on tier
      const pulseScale = 1 + Math.sin(t * 2) * 0.05 * (e.tier / 7) * multiplier;

      setPosition({ x: floatX, y: floatY });
      setRotation({ x: rotateX, y: rotateY, z: rotateZ });
      setScale(pulseScale);

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [enablePhysics, e.tier, multiplier]);

  // 3D CSS styles
  const get3DStyle = (): React.CSSProperties => {
    if (!enable3D) {
      return {
        transform: `scale(${isHovering ? 1.1 : 1})`,
        transition: 'transform 0.3s ease',
      };
    }

    return {
      transform: `
        perspective(600px)
        translateX(${position.x}px)
        translateY(${position.y}px)
        rotateX(${rotation.x}deg)
        rotateY(${rotation.y}deg)
        rotateZ(${rotation.z}deg)
        scale(${isHovering ? scale * 1.15 : scale})
      `,
      transformStyle: 'preserve-3d',
      transition: isHovering
        ? 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)'
        : 'none',
      filter: `
        drop-shadow(0 ${8 + position.y}px ${16 + Math.abs(position.y)}px rgba(0,0,0,0.3))
        brightness(${isHovering ? 1.1 : 1})
      `,
    };
  };

  // Glow effect based on tier
  const getGlowStyle = (): React.CSSProperties => {
    const glowColors: Record<number, string> = {
      1: 'rgba(158, 158, 158, 0.3)',
      2: 'rgba(96, 165, 250, 0.4)',
      3: 'rgba(52, 211, 153, 0.5)',
      4: 'rgba(167, 139, 250, 0.6)',
      5: 'rgba(244, 114, 182, 0.7)',
      6: 'rgba(251, 191, 36, 0.8)',
      7: 'rgba(255, 215, 0, 0.9)',
    };

    return {
      boxShadow: isHovering
        ? `0 0 ${30 * multiplier}px ${glowColors[e.tier] || glowColors[1]},
           0 0 ${60 * multiplier}px ${glowColors[e.tier] || glowColors[1]}`
        : `0 0 ${15 * multiplier}px ${glowColors[e.tier] || glowColors[1]}`,
      borderRadius: '50%',
      transition: 'box-shadow 0.3s ease',
    };
  };

  // Particle effect on hover (optional, can be enhanced)
  const renderParticles = () => {
    if (!isHovering || intensity === 'subtle') return null;

    const particleCount = intensity === 'intense' ? 8 : 4;
    const particles = [];

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const distance = size * 0.6;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance;

      particles.push(
        <div
          key={i}
          style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            background: e.color || '#fff',
            left: `${50 + (x / size) * 100}%`,
            top: `${50 + (y / size) * 100}%`,
            transform: 'translate(-50%, -50%)',
            opacity: 0.6,
            animation: `particleFade 1s ease-out ${i * 0.1}s infinite`,
            pointerEvents: 'none',
          }}
        />
      );
    }

    return particles;
  };

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        ...get3DStyle(),
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Glow layer */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          ...getGlowStyle(),
          pointerEvents: 'none',
        }}
      />

      {/* Original Element Canvas */}
      <ElementCanvas e={e} size={size} />

      {/* Hover particles */}
      {renderParticles()}

      {/* CSS Animations */}
      <style>{`
        @keyframes particleFade {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0);
          }
          50% {
            opacity: 0.8;
            transform: translate(-50%, -50%) scale(1.5);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
        }
      `}</style>
    </div>
  );
};

export default Enhanced3DElement;
